import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Switch } from "../components/ui/switch";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { Loader2, Store, Wine, CheckCircle2, XCircle } from "lucide-react";
import { ForaLogo, ArenaCateringLogo } from "../components/ForaLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spiritsUrl, setSpiritsUrl] = useState("");
  const [savingSpirits, setSavingSpirits] = useState(false);

  const fetchOutlets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/outlets`);
      setOutlets(res.data.outlets);
      const loze = res.data.outlets.find((o) => o.name === "Loże");
      setSpiritsUrl(loze?.spirits_menu_url || "");
    } catch {
      toast.error("Failed to load outlets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOutlets();
  }, [fetchOutlets]);

  const snackbars = outlets.filter((o) => o.zone === "kiosk");
  const loze = outlets.find((o) => o.name === "Loże");
  const activeCount = snackbars.filter((o) => o.is_active).length;

  const toggleOutlet = async (outlet, value) => {
    setOutlets((prev) =>
      prev.map((o) => (o.id === outlet.id ? { ...o, is_active: value } : o))
    );
    try {
      await axios.patch(`${API}/admin/outlets/${outlet.id}`, { is_active: value });
      toast.success(`${outlet.name} ${value ? "activated" : "deactivated"}`);
    } catch {
      toast.error("Update failed");
      fetchOutlets();
    }
  };

  const bulkSet = async (is_active) => {
    try {
      await axios.post(`${API}/admin/outlets/bulk-active`, { zone: "kiosk", is_active });
      toast.success(is_active ? "All points activated" : "All points deactivated");
      fetchOutlets();
    } catch {
      toast.error("Bulk update failed");
    }
  };

  const saveSpirits = async () => {
    if (!loze) return;
    setSavingSpirits(true);
    try {
      await axios.patch(`${API}/admin/outlets/${loze.id}`, { spirits_menu_url: spiritsUrl });
      toast.success("Spirits menu link saved");
      fetchOutlets();
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingSpirits(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-[#dd260e]" size={32} />
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto min-h-screen bg-[#f9f9f9] shadow-2xl flex flex-col">
      <Toaster position="top-center" richColors />
      <header className="bg-white border-b border-[#e8e8e8] px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <ForaLogo height="h-5" />
          <span className="text-sm font-bold">Staff Admin</span>
        </div>
        <a href="/" className="text-xs text-[#dd260e] font-semibold" data-testid="admin-back-home">
          ← App
        </a>
      </header>

      <div className="flex-1 px-4 py-4 space-y-6">
        {/* SnackBar points */}
        <section>
          <div className="flex items-center gap-2 mb-1">
            <Store size={16} className="text-[#dd260e]" />
            <h2 className="text-base font-bold">SnackBar — points of sale</h2>
          </div>
          <p className="text-xs text-gray-500 mb-3" data-testid="active-count">
            {activeCount} of {snackbars.length} active · inactive points are hidden from guests
          </p>
          <div className="flex gap-2 mb-3">
            <Button
              data-testid="activate-all-btn"
              size="sm"
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              onClick={() => bulkSet(true)}
            >
              <CheckCircle2 size={14} className="mr-1" /> Activate all
            </Button>
            <Button
              data-testid="deactivate-all-btn"
              size="sm"
              variant="outline"
              className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => bulkSet(false)}
            >
              <XCircle size={14} className="mr-1" /> Deactivate all
            </Button>
          </div>
          <div className="bg-white rounded-2xl border border-[#e8e8e8] divide-y divide-[#f0f0f0]">
            {snackbars.map((o) => (
              <div
                key={o.id}
                data-testid={`admin-row-${o.id}`}
                className="flex items-center justify-between px-4 py-3"
              >
                <div>
                  <p className={`text-sm font-semibold ${o.is_active ? "text-black" : "text-gray-400"}`}>
                    {o.name}
                  </p>
                  <p className="text-[10px] text-gray-400 truncate max-w-[180px]">{o.menu_url}</p>
                </div>
                <Switch
                  data-testid={`admin-toggle-${o.id}`}
                  aria-label={`Toggle ${o.name}`}
                  checked={o.is_active}
                  onCheckedChange={(v) => toggleOutlet(o, v)}
                />
              </div>
            ))}
          </div>
        </section>

        {/* VIP Loże spirits menu */}
        <section>
          <div className="flex items-center gap-2 mb-1">
            <Wine size={16} className="text-amber-600" />
            <h2 className="text-base font-bold">VIP Loże — Spirits menu link</h2>
          </div>
          <p className="text-xs text-gray-500 mb-3">
            External master-fb.com menu shown ONLY inside the Loże card. Leave empty to hide the button.
          </p>
          <div className="flex gap-2">
            <Input
              data-testid="spirits-url-input"
              placeholder="https://qr.master-fb.com/..."
              value={spiritsUrl}
              onChange={(e) => setSpiritsUrl(e.target.value)}
              className="flex-1 text-sm"
            />
            <Button
              data-testid="spirits-save-btn"
              size="sm"
              className="bg-[#dd260e] hover:bg-[#b91f0b] text-white"
              onClick={saveSpirits}
              disabled={savingSpirits}
            >
              {savingSpirits ? <Loader2 size={14} className="animate-spin" /> : "Save"}
            </Button>
          </div>
        </section>
      </div>

      <footer className="border-t border-[#e8e8e8] bg-white px-4 py-4 flex items-center justify-center gap-3">
        <span className="text-xs text-gray-400">Powered by</span>
        <ForaLogo height="h-4" />
        <div className="w-px h-3 bg-[#e8e8e8]" />
        <ArenaCateringLogo height="h-4" />
      </footer>
    </div>
  );
}
